import React, { useMemo, useEffect, useRef } from 'react';
import { Trash2 } from 'lucide-react';
import Button from './Button';
import { ProductionTask } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import { formatArgentinaDate } from '../../utils/dateUtils';

interface GanttViewProps {
  tasks: ProductionTask[];
  onEditTask: (task: ProductionTask) => void;
  onDeleteTask: (id: string) => void;
  getSupplierName: (id: string | null) => string;
}

const GanttView: React.FC<GanttViewProps> = ({
  tasks,
  onEditTask,
  onDeleteTask,
  getSupplierName,
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const todayMarkerRef = useRef<HTMLDivElement>(null);

  // Filter tasks with start and end dates
  const tasksWithDates = useMemo(() => {
    return tasks.filter(task => task.start_date && task.end_date);
  }, [tasks]);

  // Find date range for all tasks
  const { startDate, endDate, totalDays } = useMemo(() => {
    // Get today's date
    const today = new Date();
    
    if (tasksWithDates.length === 0) {
      // If no tasks with dates, show 15 days before and 15 days after today
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 15);
      
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + 15);
      
      return { 
        startDate, 
        endDate, 
        totalDays: 30 
      };
    }

    let minDate = new Date(tasksWithDates[0].start_date);
    let maxDate = new Date(tasksWithDates[0].end_date);

    tasksWithDates.forEach(task => {
      const taskStartDate = new Date(task.start_date!);
      const taskEndDate = new Date(task.end_date!);

      if (taskStartDate < minDate) minDate = taskStartDate;
      if (taskEndDate > maxDate) maxDate = taskEndDate;
    });

    // Ensure today is visible in the chart
    if (today < minDate) {
      minDate = new Date(today);
      minDate.setDate(today.getDate() - 5);
    } else if (today > maxDate) {
      maxDate = new Date(today);
      maxDate.setDate(today.getDate() + 5);
    }
    
    // Add buffer days (5 days on each side)
    minDate.setDate(minDate.getDate() - 5);
    maxDate.setDate(maxDate.getDate() + 5);

    // Calculate total days
    const diffTime = Math.abs(maxDate.getTime() - minDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return { startDate: minDate, endDate: maxDate, totalDays: diffDays };
  }, [tasksWithDates]);

  // Generate date headers
  const dateHeaders = useMemo(() => {
    const headers = [];
    const currentDate = new Date(startDate);
    
    for (let i = 0; i <= totalDays; i++) {
      headers.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return headers;
  }, [startDate, totalDays]);

  // Calculate task position and width
  const getTaskStyle = (task: ProductionTask) => {
    if (!task.start_date || !task.end_date) return {};

    const taskStart = new Date(task.start_date);
    const taskEnd = new Date(task.end_date);
    
    // Calculate days from project start
    const startDiff = Math.floor((taskStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Calculate task duration
    const duration = Math.ceil((taskEnd.getTime() - taskStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    // Calculate position and width percentages
    const left = (startDiff / totalDays) * 100;
    const width = (duration / totalDays) * 100;
    
    return {
      left: `${left}%`,
      width: `${width}%`,
      backgroundColor: task.status === 'pending' 
        ? '#FEF3C7' // amber-100
        : task.status === 'in_progress'
        ? '#DBEAFE' // blue-100
        : '#D1FAE5', // green-100
      borderColor: task.status === 'pending' 
        ? '#F59E0B' // amber-500
        : task.status === 'in_progress'
        ? '#3B82F6' // blue-500
        : '#10B981', // green-500
    };
  };
  
  // Calculate today marker position
  const getTodayMarkerPosition = useMemo(() => {
    const today = new Date();
    const daysDiff = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const position = (daysDiff / totalDays) * 100;
    return `${position}%`;
  }, [startDate, totalDays]);

  // Center the timeline on today's date when component mounts
  useEffect(() => {
    if (timelineRef.current && todayMarkerRef.current) {
      const timelineWidth = timelineRef.current.scrollWidth;
      const todayPosition = (parseFloat(getTodayMarkerPosition) / 100) * timelineWidth;
      const containerWidth = timelineRef.current.clientWidth;
      const scrollPosition = todayPosition - (containerWidth / 2);
      
      timelineRef.current.scrollLeft = Math.max(0, scrollPosition);
    }
  }, [getTodayMarkerPosition]);

  if (tasks.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No hay tareas registradas para esta producción
      </div>
    );
  }

  return (
    <div className="flex">
      {/* Fixed Task List Panel */}
      <div className="w-64 flex-shrink-0">
        {/* Date Header for Task List */}
        <div className="h-12 flex items-center p-2 font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
          Tarea
        </div>

        {/* Task List */}
        {tasks.map((task) => (
          <div key={task.id} className="h-12 p-2 border-b border-gray-200 dark:border-gray-700 flex flex-col justify-center">
            <div className="font-medium text-gray-900 dark:text-white text-sm">{task.task_name}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{getSupplierName(task.supplier_id)}</div>
          </div>
        ))}
      </div>

      {/* Scrollable Timeline Panel */}
      <div className="flex-1 overflow-x-auto" ref={timelineRef}>
        <div className="min-w-max">
          {/* Date Headers */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
            {dateHeaders.map((date, index) => (
              <div 
                key={index} 
                className="flex-1 min-w-[40px] p-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700"
              >
                {date.getDate()}
              </div>
            ))}
          </div>

          {/* Tasks Timeline */}
          {tasks.map((task) => (
            <div key={task.id} className="h-12 mb-0">
              <div className="relative h-12 bg-gray-100 dark:bg-gray-800 rounded">
                {/* Today marker - vertical red line that spans full height */}
                <div 
                  ref={todayMarkerRef}
                  className="absolute top-0 h-full w-0.5 bg-red-500 z-10"
                  style={{ left: getTodayMarkerPosition }}
                ></div>
                
                {task.start_date && task.end_date && (
                  <div 
                    className="absolute top-0 h-full rounded border-2 flex items-center justify-center text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity"
                    style={getTaskStyle(task)}
                    onClick={() => onEditTask(task)}
                  >
                    <span className="px-1 truncate">
                      {task.start_date && task.end_date ? 
                        `${formatArgentinaDate(task.start_date, 'dd/MM')} - ${formatArgentinaDate(task.end_date, 'dd/MM')}` : 
                        task.task_name
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tasks without dates */}
      {tasks.filter(task => !task.start_date || !task.end_date).length > 0 && (
        <div className="w-full mt-8 border-t border-gray-200 dark:border-gray-700 pt-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Tareas sin fechas asignadas</h3>
          <div className="space-y-4">
            {tasks.filter(task => !task.start_date || !task.end_date).map(task => (
              <div 
                key={task.id} 
                className={`p-4 rounded-lg shadow-sm border-l-4 ${
                  task.status === 'pending' 
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500 dark:border-amber-400' 
                    : task.status === 'in_progress'
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400'
                    : 'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400'
                }`}
                onClick={() => onEditTask(task)}
                style={{ cursor: 'pointer' }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">{task.task_name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {getSupplierName(task.supplier_id)}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Trash2 className="h-4 w-4" />}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the parent onClick
                        onDeleteTask(task.id);
                      }}
                    >
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GanttView;